import { useState, type ReactNode } from "react";
import { CurvyArrowFaq, CurvyArrowFaqMobile, CaretDownIcon } from "assets/website";

export interface FaqItem {
  question: string;
  /** Rich content is allowed — some answers are lists, not a single paragraph. */
  answer: ReactNode;
}

interface FaqProps {
  faqItems: FaqItem[];
  title?: string;
}

const Faq: React.FC<FaqProps> = ({
  faqItems,
  title = "Frequently Asked Questions"
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container mx-auto lg:px-8 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_2fr] gap-8 lg:gap-16 items-start">
          {/* Left - Title */}
          <div data-aos="fade-right" className="mt-0 lg:mt-14">
            <div className="flex items-start justify-center lg:justify-start gap-2">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-neutral-900 mb-4 lg:mb-6 text-center lg:text-left">
                {title}
              </h2>
              {/* Mobile curvy arrow */}
              <CurvyArrowFaqMobile className="w-8 h-auto lg:hidden flex-shrink-0 mt-1" />
            </div>
            {/* Desktop curvy arrow */}
            <div className="hidden lg:block">
              <CurvyArrowFaq className="w-42 h-auto text-blue-200 mt-8 ml-auto" />
            </div>
          </div>

          {/* Right - FAQ Accordion */}
          <div data-aos="fade-left" data-aos-delay="200">
            <div className="space-y-3 md:space-y-4">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F2F4F7] rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-4 md:px-6 py-4 md:py-6 flex items-center justify-between text-left"
                  >
                    <span className="text-[#1D2939] pr-3 md:pr-4 text-base md:text-xl">
                      {item.question}
                    </span>
                    <CaretDownIcon
                      className={`w-4 h-4 md:w-5 md:h-5 text-[#1D2939] flex-shrink-0 transition-transform duration-200 ${
                        openIndex === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    {/* Line separator */}
                    <div className="mx-4 md:mx-6 border-t border-[#D0D5DD]" />
                    <div className="px-4 md:px-6 py-4 text-sm md:text-base text-neutral-600 leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { Faq };
